import React from 'react';
import { observer } from 'mobx-react-lite';
import _ from 'lodash';
import styled from 'styled-components';
import { useObserver } from 'mobx-react';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { getProductsFilterCount } from 'utils/helpers/ui';
import useProductQueryParams from 'hooks/use-product-query-params';
import useMenuSortOptions from 'hooks/use-menu-sort-options';
import useUI from 'hooks/use-ui';

import ContentHeader from 'components/content-header';
import { MenuLayoutSelectorV2 } from 'src/components/menu-layout-selector-v2';
import { DrawerToggleButton } from 'components/mobile-filters-drawer';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { MenuSortV2 } from '../menu-sort-v2';
import { useMenuHeaderHeading } from './use-menu-header-heading';
import { MenuFiltersDrawer } from './menu-filters-drawer';

function MenuHeader({
  dispensary,
  category,
  page,
  collection,
  hideFilters,
  showExpiredSpecial = false,
  heading,
  description,
  special,
  customHeaderComponent,
  belowHeaderComponent,
  subheader,
}) {
  const { queryParams, setQueryParams, defaults } = useProductQueryParams();
  const router = useRouter();
  const UI = useUI();
  const showMobileFilters = useObserver(() => UI.showMobileFilters);
  const onSearchPage = useObserver(() => UI.onSearchPage);
  const onBrandsPage = _.includes(router?.route, 'brands');
  const filterCount = getProductsFilterCount({ queryParams, category, onBrandsPage });
  const offerSpecialPage = _.includes(router?.route, 'offer');
  const saleSpecialPage = _.includes(router?.route, 'sale');
  const flags = useFlags();
  const shouldDisableSearch = flags['ecomm.configuration.disable-product-search'] ?? false;
  const isMenuLayoutAvailable = useMediaQuery(`(min-width: 365px)`);

  const isBrands = router?.pathname?.includes('brands');
  const isKioskProEnabled = useKioskPro();
  const { isKiosk } = useUI();
  const isKioskProBrandsPage = isKioskProEnabled && isKiosk && isBrands;

  // ECOMM-14099: the category-page "All <Category>" heading is a back control. It used to call
  // router.back() unconditionally, which strands users who open a filtered category URL
  // directly -- with no in-app history the click does nothing and the filters stay applied.
  // When product filters are active, clear them instead so the heading always returns to the
  // full category; with no filters applied it keeps the historical back-navigation behavior.
  // Only the filter facets are reset (never menuType/search/sortby/category) and only on a
  // plain category route -- subcategory routes need a real navigation to change the listing.
  const filterFacetKeys = [
    'brands',
    'subcategories',
    'straintypes',
    'weight',
    'effects',
    'terpenes',
    'specialFilter',
    'tags',
    'smartTags',
    'pricerange',
    'potencythc',
    'potencycbd',
  ];
  const headingShouldClearFilters =
    page === 'category' &&
    !onSearchPage &&
    !router?.query?.subcategory &&
    _.some(filterFacetKeys, (key) => !_.isEqual(queryParams[key], defaults[key]));

  function handleHeadingBackNavigation() {
    setQueryParams({ ..._.pick(defaults, filterFacetKeys), page: defaults.page });
  }

  let menuHeading = useMenuHeaderHeading({
    category,
    page,
    collection,
  });
  menuHeading = heading ?? menuHeading;

  // preload
  useMenuSortOptions();

  return (
    <ContentHeader
      page={page}
      heading={menuHeading}
      description={description}
      searchString={shouldDisableSearch ? false : queryParams.search}
      dispensary={dispensary}
      isMobileEcommApp={UI.isMobileEcommApp}
      selectedSpecial={special}
      offerSpecialPage={offerSpecialPage}
      saleSpecialPage={saleSpecialPage}
      showExpiredSpecial={showExpiredSpecial}
      customHeaderComponent={customHeaderComponent}
      belowHeaderComponent={belowHeaderComponent}
      subheader={subheader}
      onBackNavigation={headingShouldClearFilters ? handleHeadingBackNavigation : undefined}
    >
      <FiltersContainer data-testid='filters-button'>
        {!(hideFilters && !onBrandsPage) && (
          <DesktopOnly>
            <MenuSortV2 />
            <LayoutSelectorContainer>
              <MenuLayoutSelectorV2 />
            </LayoutSelectorContainer>
          </DesktopOnly>
        )}
        {!hideFilters && (
          <MobileOnly>
            <MobileFiltersLayoutContainer>
              <DrawerToggleButton onClick={() => (UI.showMobileFilters = true)} filterCount={filterCount} />
              {isKioskProBrandsPage && <MenuSortV2 />}
              {isMenuLayoutAvailable && <MenuLayoutSelectorV2 hideLabel />}
            </MobileFiltersLayoutContainer>

            <MenuFiltersDrawer open={showMobileFilters} onClose={() => (UI.showMobileFilters = false)} />
          </MobileOnly>
        )}
      </FiltersContainer>
    </ContentHeader>
  );
}

export default observer(MenuHeader);

const FiltersContainer = styled.div`
  align-items: center;
  display: flex;
  font-family: var(--dct--customized-fonts-secondary);
  margin-left: auto;
`;

const LayoutSelectorContainer = styled.div`
  align-items: center;
  display: flex;
  margin-left: 24px;
`;

const MobileFiltersLayoutContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;
